import { Injectable } from '@angular/core';
import { DestinationSearchParams } from './params/destination-search.params';
import { DestinationService } from './destination.service';
import { Observable, throwError } from 'rxjs';
import { DestinationResponse } from './responses/destination.response';
import { PaginatedResponse } from '../../../shared/models/paginated-response';
import { DestinationAction } from './model/destination-action.enum';
import { LoadingIndicatorService } from '../../../services/loading-indicator/loading-indicator.service';
import { catchError, tap } from 'rxjs/operators';
import { EventsService } from '../../../services/events/events.service';
import { BsModalService } from 'ngx-bootstrap';
import { BotMergeTransferFinishedStatsModal } from './components/modal/bot-merge-transfer-finished-stats-modal/bot-merge-transfer-finished-stats-modal.component';
import { InProgressStatsModal } from './components/modal/in-progress-stats-modal/in-progress-stats-modal.component';

@Injectable({
    providedIn: 'root',
})
export class DestinationUtilsService {
    constructor(
        private loadingIndicator: LoadingIndicatorService,
        private eventsService: EventsService,
        private bsModalService: BsModalService,
        private destinationService: DestinationService
    ) {}

    loadDestinations(
        params: DestinationSearchParams
    ): Observable<PaginatedResponse<DestinationResponse>> {
        this.loadingIndicator.showLoadingBar();
        return this.destinationService.index(params).pipe(
            tap(() => this.loadingIndicator.hideLoadingBar()),
            catchError((err) => {
                this.loadingIndicator.hideLoadingBar();
                this.eventsService.broadcast(
                    'requestShowErrorMessage',
                    JSON.stringify(err)
                );
                return throwError(err);
            })
        );
    }

    actionClicked(data: {
        action: DestinationAction;
        destination: DestinationResponse;
    }) {
        const { action, destination } = data;
        this.loadingIndicator.showLoadingBar();
        switch (action) {
            case DestinationAction.Prepare:
                if (!destination.botMergeContainer || !destination.botMergeContainer.isActive) {
                    this.destinationService
                        .startPrepare(destination.boundary._id)
                        .subscribe(
                            () => {
                                this.loadingIndicator.hideLoadingBar();
                                if (!destination.botMergeContainer) {
                                  destination.botMergeContainer = {} as any;
                                }
                                destination.botMergeContainer.isActive = true;
                            },
                            (err) => {
                                this.loadingIndicator.hideLoadingBar();
                                this.eventsService.broadcast(
                                    'requestShowErrorMessage',
                                    JSON.stringify(err)
                                );
                            }
                        );
                } else {
                    this.destinationService
                        .stopPrepare(destination.boundary._id)
                        .subscribe(
                            () => {
                                this.loadingIndicator.hideLoadingBar();
                                if (!destination.botMergeContainer) {
                                  destination.botMergeContainer = {} as any;
                                }
                                destination.botMergeContainer.isActive = false;
                            },
                            (err) => {
                                this.loadingIndicator.hideLoadingBar();
                                this.eventsService.broadcast(
                                    'requestShowErrorMessage',
                                    JSON.stringify(err)
                                );
                            }
                        );
                }
                break;
            case DestinationAction.ImportStats:
                this.loadAndShowImportStatsInModal(destination.boundary._id);
                break;
            case DestinationAction.InProgressStats:
                this.loadAndShowInProgressStatsInModal(
                    destination.boundary._id
                );
                break;
            case DestinationAction.Download:
                this.downloadHousingData(destination.boundary._id);
                break;
            case DestinationAction.Import:
                this.destinationService
                    .startImport(destination.boundary._id)
                    .subscribe(
                        () => {
                            this.loadingIndicator.hideLoadingBar();
                        },
                        (err) => {
                            this.loadingIndicator.hideLoadingBar();
                            this.eventsService.broadcast(
                                'requestShowErrorMessage',
                                JSON.stringify(err)
                            );
                        }
                    );
                break;
            default:
                this.loadingIndicator.hideLoadingBar();
                console.error('Unknown action: ' + action);
        }
    }

    downloadHousingData(destinationId: string) {
        this.loadingIndicator.showLoadingBar();
        this.destinationService.downloadHousingData(destinationId).subscribe(
            (blob) => {
                this.loadingIndicator.hideLoadingBar();
                this.loadingIndicator.hideLoadingBar();
                const downloadLink = document.createElement('a');
                const url = URL.createObjectURL(blob);
                downloadLink.href = url;
                downloadLink.download = `stats-${destinationId}.xlsx`;
                // Append to body to work in Firefox
                document.body.appendChild(downloadLink);
                downloadLink.click();
                // Clean up
                setTimeout(() => {
                    document.body.removeChild(downloadLink);
                    URL.revokeObjectURL(url); // Free up memory
                }, 100);
            },
            (err) => {
                this.loadingIndicator.hideLoadingBar();
                this.eventsService.broadcast(
                    'requestShowErrorMessage',
                    JSON.stringify(err)
                );
            }
        );
    }

    loadAndShowInProgressStatsInModal(destinationId: string) {
        this.loadingIndicator.showLoadingBar();
        this.destinationService.show(destinationId).subscribe(
            (res) => {
                this.loadingIndicator.hideLoadingBar();
                this.bsModalService.show(InProgressStatsModal, {
                    initialState: {
                        stats: res.statistics,
                    },
                });
            },
            (err) => {
                this.loadingIndicator.hideLoadingBar();
                this.eventsService.broadcast(
                    'requestShowErrorMessage',
                    JSON.stringify(err)
                );
            }
        );
    }

    loadAndShowImportStatsInModal(destinationId: string) {
        this.loadingIndicator.showLoadingBar();
        this.destinationService.importStatistics(destinationId).subscribe(
            (stats) => {
                this.loadingIndicator.hideLoadingBar();
                this.bsModalService.show(BotMergeTransferFinishedStatsModal, {
                    initialState: {
                        stats: stats,
                    },
                });
            },
            (err) => {
                this.loadingIndicator.hideLoadingBar();
                this.eventsService.broadcast(
                    'requestShowErrorMessage',
                    JSON.stringify(err)
                );
            }
        );
    }
}
